import { ReactNode, useEffect, useRef, useState } from "react";
import {
  CalendarDays,
  ChevronsDownUp,
  ChevronsUpDown,
  Clock,
  Film,
  ImagePlus,
  Play,
  QrCode,
  Star,
  Type,
  Undo2,
  UserRound,
  Youtube,
} from "lucide-react";
import { Slide } from "@shared/types";
import { api, ApiError, MediaUploadResult, SharedGroupRef } from "@/lib/api";
import {
  makeClockSlide,
  makeHolidaySlides,
  makeQrSlide,
  makeReviewSlide,
  makeTeamSlide,
  makeTextSlide,
  makeVimeoSlide,
  makeYouTubeSlide,
  slideFromUpload,
} from "@/lib/slide-factory";
import {
  appendSlides,
  applyUndo,
  duplicateSlides,
  groupSlideIds,
  groupSummaries,
  moveSlide,
  OpResult,
  pruneSelection,
  pushUndo,
  removeSlides,
  setSlidesEnabled,
  UndoEntry,
} from "@/lib/slide-list-ops";
import { formatShortDate, usHolidays } from "@/lib/holidays";
import {
  loadHolidayClosures,
  loadHolidayPhone,
  saveHolidayClosures,
  saveHolidayPhone,
} from "@/lib/holiday-prefs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SlideCard } from "@/components/admin/SlideCard";
import { GroupRefCard } from "@/components/admin/GroupRefCard";
import { SlideExpandViewer } from "@/components/admin/SlideExpandViewer";

interface SlideListEditorProps {
  slides: Slide[];
  onSlidesChange: (slides: Slide[]) => void;
  /** Upload a file to the right place (workspace vs shared group). */
  uploadMedia: (file: File) => Promise<MediaUploadResult>;
  defaultDuration: number;
  /** Referenced shared groups, for rendering group-reference cards. */
  sharedGroups?: Record<string, SharedGroupRef>;
  /** Extra toolbar controls (e.g. "Add shared group"). */
  toolbarExtra?: ReactNode;
  /** Brand colors applied to new text slides, if enabled. */
  textDefaults?: { bgColor: string; textColor: string };
}

/** Ctrl+Z inside these must stay the browser's own text undo. */
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLInputElement) return target.type !== "checkbox" && target.type !== "radio";
  return target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}

const slidesWord = (n: number) => `${n} slide${n === 1 ? "" : "s"}`;

export function SlideListEditor({
  slides,
  onSlidesChange,
  uploadMedia,
  defaultDuration,
  sharedGroups,
  toolbarExtra,
  textDefaults,
}: SlideListEditorProps) {
  const mediaInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  // Tracks the slides the admin has folded away rather than the open ones, so
  // anything added later (new, uploaded, duplicated) shows up open by default.
  const [collapsed, setCollapsed] = useState(() => new Set(slides.map((s) => s.id)));
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [closures, setClosures] = useState(() => new Set(loadHolidayClosures()));
  const [phone, setPhone] = useState(loadHolidayPhone);
  // A cleared/typo'd year (0, 202, 20026) would generate schedules that never
  // match — or worse, "Closed" slides that play year-round.
  const yearValid = Number.isInteger(year) && year >= 2000 && year <= 2100;
  const yearHolidays = yearValid ? usHolidays(year) : [];
  const closureCount = yearHolidays.filter((h) => closures.has(h.name)).length;

  // Always-current slides, so a slow multi-file video upload doesn't revert
  // edits/reorders the admin made while it was running.
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  const selection = pruneSelection(selected, slides);
  useEffect(() => {
    setSelected((prev) => pruneSelection(prev, slides));
  }, [slides]);

  const groups = groupSummaries(slides);
  const lastUndo = undoStack[undoStack.length - 1];

  function commit(result: OpResult) {
    if (!result) return;
    onSlidesChange(result.slides);
    setUndoStack((stack) => pushUndo(stack, result.undo));
  }

  function undo(): boolean {
    if (!lastUndo) return false;
    setUndoStack((stack) => stack.slice(0, -1));
    onSlidesChange(applyUndo(slidesRef.current, lastUndo));
    return true;
  }

  // The full-screen slide viewer isn't a dialog element but still covers the list.
  const keyUndo = () => expandedIndex === null && undo();
  const undoRef = useRef(keyUndo);
  undoRef.current = keyUndo;
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== "z") return;
      if (isTextEntry(e.target) || document.querySelector('[role="dialog"]')) return;
      // The list stays mounted behind other editor tabs; don't undo out of sight.
      if (rootRef.current?.closest('[data-state="inactive"]')) return;
      if (undoRef.current()) e.preventDefault();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const update = (id: string, slide: Slide) =>
    onSlidesChange(slides.map((s) => (s.id === id ? slide : s)));
  const remove = (id: string) => commit(removeSlides(slides, [id]));
  const move = (index: number, dir: -1 | 1) => commit(moveSlide(slides, index, index + dir));
  const moveTo = (index: number, edge: "top" | "bottom") =>
    commit(moveSlide(slides, index, edge === "top" ? 0 : slides.length - 1));
  const duplicate = (id: string) => commit(duplicateSlides(slides, [id]));
  const addText = () => onSlidesChange([...slides, makeTextSlide(defaultDuration, textDefaults)]);
  const addClock = () => onSlidesChange([...slides, makeClockSlide(defaultDuration)]);
  const addYouTube = () => onSlidesChange([...slides, makeYouTubeSlide("", defaultDuration)]);
  const addVimeo = () => onSlidesChange([...slides, makeVimeoSlide("", defaultDuration)]);
  const addReview = () => onSlidesChange([...slides, makeReviewSlide(defaultDuration)]);
  const addQr = () => onSlidesChange([...slides, makeQrSlide(defaultDuration)]);
  const addTeam = () => onSlidesChange([...slides, makeTeamSlide(defaultDuration)]);

  const toggleCollapsed = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const setRowSelected = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const allSelected = slides.length > 0 && selection.size === slides.length;
  const someSelected = selection.size > 0 && !allSelected;

  function bulkEnable(on: boolean) {
    commit(setSlidesEnabled(slides, selection, on, `turn ${on ? "on" : "off"} ${slidesWord(selection.size)}`));
  }

  function toggleGroup(name: string, on: boolean) {
    commit(setSlidesEnabled(slides, groupSlideIds(slides, name), on, `turn ${on ? "on" : "off"} “${name}”`));
  }

  function toggleClosure(name: string, on: boolean) {
    const next = new Set(closures);
    if (on) next.add(name);
    else next.delete(name);
    setClosures(next);
    saveHolidayClosures([...next]);
  }

  function addHolidays() {
    const group = `Holidays ${year}`;
    // Closure days this year's holiday slides already cover, so clicking
    // "Add" twice doesn't stack duplicate "Closed" slides.
    const existing = new Set(
      slides
        .filter((s) => s.type !== "groupref" && s.group === group && s.schedule?.endDate)
        .map((s) => (s as { schedule: { endDate: string } }).schedule.endDate),
    );
    const added = makeHolidaySlides(year, defaultDuration, {
      selected: yearHolidays.filter((h) => closures.has(h.name)).map((h) => h.name),
      phone,
      colors: textDefaults,
    }).filter((s) => !existing.has(s.schedule?.endDate ?? ""));
    if (added.length) commit(appendSlides(slides, added, "add holidays"));
    else setError(`Those ${year} holiday slides are already in the list.`);
    setHolidayOpen(false);
  }

  async function onMediaSelected(files: FileList | null) {
    if (!files) return;
    setUploading(true);
    setError(null);
    // Upload files one by one and keep every success: one bad file (e.g. an
    // iPhone HEIC) must not throw away the photos uploaded before it.
    const added: Slide[] = [];
    const failures: string[] = [];
    try {
      for (const file of Array.from(files)) {
        try {
          const res = await uploadMedia(file);
          const slide = slideFromUpload(res, defaultDuration);
          if (slide) added.push(slide);
        } catch (err) {
          failures.push(`${file.name}: ${err instanceof ApiError ? err.message : "upload failed"}`);
        }
      }
    } finally {
      if (added.length) onSlidesChange([...slidesRef.current, ...added]);
      if (failures.length) {
        setError(
          `${failures.length === 1 ? "1 file was" : `${failures.length} files were`} not added — ` +
            failures.join(" · "),
        );
      }
      setUploading(false);
      if (mediaInput.current) mediaInput.current.value = "";
    }
  }

  return (
    <div ref={rootRef} className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => mediaInput.current?.click()} disabled={uploading}>
          <ImagePlus className="mr-1 h-4 w-4" /> {uploading ? "Uploading…" : "Add media"}
        </Button>
        <Button variant="outline" onClick={addText}>
          <Type className="mr-1 h-4 w-4" /> Add text slide
        </Button>
        <Button variant="outline" onClick={addClock}>
          <Clock className="mr-1 h-4 w-4" /> Add clock/weather
        </Button>
        <Button variant="outline" onClick={addYouTube}>
          <Youtube className="mr-1 h-4 w-4" /> Add YouTube
        </Button>
        <Button variant="outline" onClick={addVimeo}>
          <Film className="mr-1 h-4 w-4" /> Add Vimeo
        </Button>
        <Button variant="outline" onClick={addReview}>
          <Star className="mr-1 h-4 w-4" /> Add review
        </Button>
        <Button variant="outline" onClick={addQr}>
          <QrCode className="mr-1 h-4 w-4" /> Add QR code
        </Button>
        <Button variant="outline" onClick={addTeam}>
          <UserRound className="mr-1 h-4 w-4" /> Add team member
        </Button>
        <Dialog open={holidayOpen} onOpenChange={setHolidayOpen}>
          <DialogTrigger asChild>
            <Button variant="outline">
              <CalendarDays className="mr-1 h-4 w-4" /> Add holidays
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add holiday closures</DialogTitle>
              <DialogDescription>
                Tick the days the office is closed. Each gets a “Closed” slide that shows for the
                week before, says when you reopen, and lands in a “Holidays” group you can switch on
                or off in one click.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div className="space-y-2">
                <Label htmlFor="holiday-year">Year</Label>
                <Input
                  id="holiday-year"
                  type="number"
                  value={year || ""}
                  onChange={(e) => setYear(Number(e.target.value))}
                />
                {!yearValid && (
                  <p className="text-sm text-destructive">Enter a year between 2000 and 2100.</p>
                )}
              </div>
              {yearValid && (
                <fieldset className="space-y-1">
                  <legend className="mb-1 text-sm font-medium">Closed on</legend>
                  <div className="max-h-[40vh] space-y-1 overflow-y-auto rounded-md border p-2">
                    {yearHolidays.map((h) => (
                      <label key={h.name} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-primary"
                          checked={closures.has(h.name)}
                          onChange={(e) => toggleClosure(h.name, e.target.checked)}
                        />
                        <span>
                          {h.name}{" "}
                          <span className="text-muted-foreground">
                            — {formatShortDate(h.observed)}
                            {h.observed !== h.date && " (observed)"}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
              <div className="space-y-2">
                <Label htmlFor="holiday-phone">Phone for urgent needs (optional)</Label>
                <Input
                  id="holiday-phone"
                  type="tel"
                  value={phone}
                  placeholder="(808) 555-0123"
                  onChange={(e) => {
                    setPhone(e.target.value);
                    saveHolidayPhone(e.target.value);
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button disabled={!yearValid || closureCount === 0} onClick={addHolidays}>
                Add {closureCount} closure slide{closureCount === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {toolbarExtra}
        <input
          ref={mediaInput}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => void onMediaSelected(e.target.files)}
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {slides.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="mr-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e) => setSelected(e.target.checked ? new Set(slides.map((s) => s.id)) : new Set())}
            />
            Select all
          </label>
          <Button size="sm" variant="ghost" onClick={() => setCollapsed(new Set())}>
            <ChevronsUpDown className="mr-1 h-4 w-4" /> Expand all
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCollapsed(new Set(slides.map((s) => s.id)))}>
            <ChevronsDownUp className="mr-1 h-4 w-4" /> Collapse all
          </Button>
          {lastUndo && (
            <Button size="sm" variant="outline" onClick={() => undo()} title="Ctrl+Z / Cmd+Z">
              <Undo2 className="mr-1 h-4 w-4" /> Undo {lastUndo.label}
            </Button>
          )}
        </div>
      )}
      {slides.length === 0 && lastUndo && (
        <Button size="sm" variant="outline" onClick={() => undo()} title="Ctrl+Z / Cmd+Z">
          <Undo2 className="mr-1 h-4 w-4" /> Undo {lastUndo.label}
        </Button>
      )}

      {selection.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border bg-background p-2 shadow-sm">
          <span className="px-1 text-sm font-medium">{selection.size} selected</span>
          <Button size="sm" variant="outline" onClick={() => bulkEnable(true)}>
            Turn on
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulkEnable(false)}>
            Turn off
          </Button>
          <Button size="sm" variant="outline" onClick={() => commit(duplicateSlides(slides, selection))}>
            Duplicate
          </Button>
          <Button size="sm" variant="outline" onClick={() => commit(removeSlides(slides, selection))}>
            Remove
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear selection
          </Button>
        </div>
      )}

      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-2">
          <span className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Groups
          </span>
          {groups.map((g) => (
            <div key={g.name} className="flex items-center gap-2 rounded-md border bg-background px-2 py-1 text-sm">
              <span className="max-w-[14rem] truncate">{g.name}</span>
              <span className="text-xs text-muted-foreground">{g.count}</span>
              <Switch
                checked={g.enabled}
                onCheckedChange={(v) => toggleGroup(g.name, v)}
                aria-label={`Turn group ${g.name} on or off`}
              />
            </div>
          ))}
        </div>
      )}

      {slides.length === 0 ? (
        <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
          <Play className="mx-auto mb-2 h-8 w-8 opacity-50" />
          No slides yet. Add images, videos, or a text slide to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {slides.map((slide, i) => {
            const row = {
              index: i,
              total: slides.length,
              collapsed: collapsed.has(slide.id),
              onToggleCollapsed: () => toggleCollapsed(slide.id),
              selected: selection.has(slide.id),
              onSelectedChange: (on: boolean) => setRowSelected(slide.id, on),
              onRemove: () => remove(slide.id),
              onMove: (dir: -1 | 1) => move(i, dir),
              onMoveTo: (edge: "top" | "bottom") => moveTo(i, edge),
              onDuplicate: () => duplicate(slide.id),
            };
            return slide.type === "groupref" ? (
              <GroupRefCard
                key={slide.id}
                {...row}
                slide={slide}
                group={sharedGroups?.[slide.sharedGroupId]}
                onChange={(s) => update(slide.id, s)}
              />
            ) : (
              <SlideCard
                key={slide.id}
                {...row}
                slide={slide}
                uploadMedia={uploadMedia}
                onChange={(s) => update(slide.id, s)}
                onExpand={() => setExpandedIndex(i)}
              />
            );
          })}
        </div>
      )}

      {expandedIndex !== null && (
        <SlideExpandViewer
          slides={slides}
          startIndex={expandedIndex}
          sharedGroups={sharedGroups}
          onClose={() => setExpandedIndex(null)}
        />
      )}
    </div>
  );
}

/** Convenience wrappers so callers don't repeat the endpoint wiring. */
export const workspaceUploader = (slug: string) => (file: File) => api.uploadMedia(slug, file);
export const sharedGroupUploader = (id: string) => (file: File) =>
  api.uploadSharedGroupMedia(id, file);
